import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal, useSnackBar } from "@truevault/aquilo";
import { StandardPageWrapper } from "../../components/layout/StandardPageWrapper";
import {
  ConsentBannerSettings,
  ConsentBannerSettingsHandle,
  useConsentBannerSettings,
  useConsentBannerStagingSettings,
} from "../../components/superorg/settings/ConsentBannerSettings";
import { usePrimaryOrganization, usePrimaryOrganizationId } from "../../hooks";
import { CMPSettingsDto } from "../../../common/service/server/dto/CMPSettingsDto";
import { Api } from "../../../common/service/Api";
import { RouteHelpers, Routes } from "../../root/routes";
import { Link } from "react-router-dom";
import { TopNavigationBreadcrumb } from "../../components/privacyCenter/TopNavigationBreadcrumb";
import { TabPanel } from "../../components";
import { CookieScannerTab } from "../stay-compliant/CookieScannerTab";
import { useCookieScanner } from "../../components/organisms/cookies/CookieScanner";
import { useHistory, useLocation } from "react-router";
import { TrueScanTab } from "../stay-compliant/TrueScanTab";
import { useOrganizationFeatureCookieConsent } from "../../hooks/useOrganizationFeatures";
import { ConsentRecordListingWrapper } from "../../components/organisms/consent/ConsentRecordListing";
import { ConsumerConsentLookup } from "../../components/organisms/consent/ConsumerConsentLookup";
import { GpcOptOutRecords } from "../../components/organisms/consent/GpcOptOutRecords";
import TabsWithUnderline from "@truevault/aquilo/src/components/tabs/TabsWithUnderline";
import { useOnboardingProgress } from "../../../common/hooks/useOnboardingProgress";
import { ProgressFooter, ProgressFooterActions } from "../get-compliant/ProgressFooter";
import {
  ConsentBannerProductionHelpCard,
  ConsentBannerStagingHelpCard,
} from "../../components/organisms/consentBanner/ConsentBannerHelpCards";
import { ConsentNote } from "../../components/organisms/consentBanner/ConsentNote";
import {
  ConsentEnvironment,
  getStagingScriptUrl,
} from "../../components/organisms/consentBanner/consentEnvironment";

export const PrivacyCenterConsentBanner = () => {
  const routerHistory = useHistory();
  const [organization, orgRequest] = usePrimaryOrganization();
  const organizationId = usePrimaryOrganizationId();
  // Which site's consent banner the Consent Banner tab is editing and publishing to.
  const [environment, setEnvironment] = useState<ConsentEnvironment>("production");
  // Switching environments remounts the form, so put focus back on the switcher afterwards.
  const [focusEnvironmentSwitcher, setFocusEnvironmentSwitcher] = useState(false);
  const changeEnvironment = useCallback((nextEnvironment: ConsentEnvironment) => {
    setEnvironment(nextEnvironment);
    setFocusEnvironmentSwitcher(true);
  }, []);
  // Leaving the Consent Banner tab unmounts the form, so ask first when it has unpublished changes.
  const [hasUnpublishedConsentChanges, setHasUnpublishedConsentChanges] = useState(false);
  const [pendingTab, setPendingTab] = useState<string | null>(null);
  // Set right before navigating away on purpose, e.g. after the onboarding footer publishes.
  const allowNavigationRef = useRef(false);

  // Leaving the page (nav links, reloads, closing the tab) would also discard unpublished changes.
  useEffect(() => {
    if (!hasUnpublishedConsentChanges) {
      return;
    }

    const warningMessage = `Your consent banner changes to ${environment} aren't published. Leave this page and discard them?`;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = warningMessage;
      return warningMessage;
    };

    const unblock = routerHistory.block(() =>
      allowNavigationRef.current ? undefined : warningMessage,
    );
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      unblock();
    };
  }, [hasUnpublishedConsentChanges, environment, routerHistory]);

  const showCookieLookup = useOrganizationFeatureCookieConsent(organization);

  // Ref for calling the consent banner form's submit method
  const consentBannerSettingsRef = useRef<ConsentBannerSettingsHandle>();

  // Check if the org is still in onboarding (hasn't completed website consent setup)
  const [onboardingProgress] = useOnboardingProgress(organizationId);
  const isWebsiteConsentOnboardingComplete =
    onboardingProgress?.privacyCenterOnboardingProgress?.isWebsiteConsentSetup ?? false;

  const showTrueScanTab = organization?.featureTrueScan === true;

  let tabOptions: { label: React.ReactNode; value: string }[] = [
    { label: "Consent Banner", value: "consentbannersettings" },
    { label: "Cookie Scan", value: "cookiescanner" },
  ];

  if (showTrueScanTab) {
    tabOptions.push({ label: "TrueScan", value: "truescan" });
  }

  if (showCookieLookup) {
    tabOptions.push({
      label: "Website Consent Records",
      value: "consentrecords",
    });
    tabOptions.push({
      label: "Consumer Consent Lookup",
      value: "consumerconsentlookup",
    });
  }

  tabOptions.push({
    label: "GPC Opt-Outs",
    value: "gpcoptouts",
  });

  const location = useLocation();
  const [tab, setTab] = useState<string>(
    location.pathname === Routes.cookies.root
      ? "cookiescanner"
      : location.pathname === Routes.records.websiteConsentRecords
      ? "consentrecords"
      : location.pathname === Routes.records.consumerConsentLookup
      ? "consumerconsentlookup"
      : location.pathname === Routes.records.gpcOptOuts
      ? "gpcoptouts"
      : location.pathname === Routes.trueScan.root
      ? "truescan"
      : "consentbannersettings",
  );

  const tabNameByValue: Record<string, string> = {
    consentbannersettings: "Consent Banner",
    cookiescanner: "Cookie Scan",
    truescan: "TrueScan",
    consentrecords: "Website Consent Records",
    consumerconsentlookup: "Consumer Consent Lookup",
    gpcoptouts: "GPC Opt-Outs",
  };

  useEffect(() => {
    const route = tab;
    if (route === "cookiescanner") {
      history.replaceState(null, null, Routes.cookies.root);
    } else if (route === "consentbannersettings") {
      history.replaceState(null, null, Routes.privacyCenter.consentBanner);
    } else if (route === "consentrecords") {
      history.replaceState(null, null, Routes.records.websiteConsentRecords);
    } else if (route === "consumerconsentlookup") {
      history.replaceState(null, null, Routes.records.consumerConsentLookup);
    } else if (route === "gpcoptouts") {
      history.replaceState(null, null, Routes.records.gpcOptOuts);
    } else if (route === "truescan") {
      history.replaceState(null, null, Routes.trueScan.root);
    }
  }, [tab]);

  //
  // Consent banner settings hooks
  //
  const { settings, privacyCenter, privacyCenterRequest, cmpCustomizationRequest, getLatestData } =
    useConsentBannerSettings();
  const { stagingSettings, stagingSettingsRequest, getLatestStagingData } =
    useConsentBannerStagingSettings(privacyCenter?.id);
  // A failed load must not look like "never published": a staging publish replaces the whole saved record.
  const stagingLoadFailed = stagingSettingsRequest.error;

  // Staging edits its own saved settings, starting from production's until its first publish. The staging response
  // leaves out `orgInfo` since it describes the org, so take it from the live settings.
  const environmentSettings: CMPSettingsDto =
    environment === "staging"
      ? { ...(stagingSettings ?? settings), orgInfo: settings.orgInfo }
      : settings;

  const neverPublished = !(
    settings.canadaConsentManagementVariant ||
    settings.usConsentManagementVariantNonUSP ||
    settings.usConsentManagementVariantCA ||
    settings.usConsentManagementVariantUSP ||
    settings.usConsentManagementVariantMinors ||
    settings.eeaukConsentManagementVariant
  );

  const updatePrivacyCenterCMPCustomization = useCallback(
    (submitValues: CMPSettingsDto) =>
      Api.privacyCenter
        .postPrivacyCenterCMPCustomization(organization?.id, privacyCenter?.id, submitValues)
        // Wait for the saved settings to come back, so the form doesn't show the publish as unpublished meanwhile.
        .then(() => Promise.all([getLatestData(), getLatestStagingData()]))
        .then(() => undefined),
    [organization?.id, privacyCenter?.id, getLatestData, getLatestStagingData],
  );

  const enqueueSnackbar = useSnackBar();
  // Asked on staging before finishing onboarding, when production doesn't have the settings being published.
  const [confirmProductionPublishOpen, setConfirmProductionPublishOpen] = useState(false);
  const productionNeedsPublish = () =>
    neverPublished || consentBannerSettingsRef.current?.differsFrom(settings) === true;

  // Publishes the environment being edited, runs `afterPublish` if given, then moves on to the dashboard if both worked.
  const finishBannerConfiguration = async (afterPublish?: () => Promise<boolean>) => {
    if (!consentBannerSettingsRef.current) {
      return;
    }
    const success = await consentBannerSettingsRef.current.submitForm();
    // Only navigate if publish was successful
    if (success && (!afterPublish || (await afterPublish()))) {
      // The form may not have reported its publish as saved yet.
      allowNavigationRef.current = true;
      routerHistory.push(Routes.privacyCenter.dashboard + "?confetti=1");
    }
  };

  // Publishes staging, then the same settings to production.
  const publishStagingAndProduction = async () => {
    setConfirmProductionPublishOpen(false);
    // Read before publishing staging, which resets the form to the saved settings.
    const stagingPublishSettings = consentBannerSettingsRef.current?.getPublishedSettings();
    if (!stagingPublishSettings) {
      return;
    }
    await finishBannerConfiguration(async () => {
      try {
        await updatePrivacyCenterCMPCustomization({
          ...stagingPublishSettings,
          isStagingPublish: false,
          // The preview page is only set on production.
          previewURL: settings.previewURL,
        });
      } catch (error) {
        enqueueSnackbar("Couldn't publish to production. Try again.", { variant: "error" });
        return false;
      }
      enqueueSnackbar("Published to production", { variant: "success" });
      return true;
    });
  };

  // Keep track of whether to show the footer in a separate state variable that is only initialized on page load, so we know to persist the footer even after the user has completed this step
  const [showFooter, setShowFooter] = useState<boolean | null>(null);
  useEffect(() => {
    if (showFooter === null && onboardingProgress) {
      // Show footer if org hasn't completed the website consent onboarding step
      setShowFooter(!isWebsiteConsentOnboardingComplete);
    }
  }, [onboardingProgress, showFooter, setShowFooter, isWebsiteConsentOnboardingComplete]);

  //
  // Cookie scanner hooks
  //
  const scanner = useCookieScanner();

  const developerInstructionsUrl = RouteHelpers.instructions.id(organization?.devInstructionsId);

  const consentBannerIntro = (
    <>
      {environment === "production" ? (
        <ConsentBannerProductionHelpCard />
      ) : (
        <ConsentBannerStagingHelpCard
          stagingScriptUrl={getStagingScriptUrl(privacyCenter?.id)}
          developerInstructionsUrl={developerInstructionsUrl}
        />
      )}
      {environment === "staging" && stagingLoadFailed && (
        <ConsentNote tone="warning">
          We couldn't load your staging settings, so publishing to staging is off for now. Reload
          the page to try again.
        </ConsentNote>
      )}
      {neverPublished && (
        <ConsentNote>
          <div className="font-medium">
            In order to customize your consent banner, your website must be using the latest version
            of the Polaris JavaScript. To do so, complete the following steps:
          </div>
          <ol className="mb-0 mt-[8px] flex list-decimal flex-col gap-[4px] pl-[20px]">
            <li>
              Input your Google Analytics Measurement ID in your{" "}
              <Link to={Routes.privacyCenter.integrations}>Integrations</Link>
            </li>
            <li>
              Update the Polaris JavaScript on your site found in Step 2 of your{" "}
              <a href={developerInstructionsUrl} target="_blank" rel="noreferrer noopener">
                Developer Instructions
              </a>
            </li>
            <li>
              Run the <a href={Routes.websiteAudit.root}>Website Audit</a> to ensure your JavaScript
              was updated correctly
            </li>
            <li>
              Follow our{" "}
              <a
                href="https://developers.truevault.com/docs/google-tag-manager/setting-up-the-consent-banner"
                target="_blank"
                rel="noreferrer noopener"
              >
                GTM setup
              </a>{" "}
              article to ensure third-party scripts on your website are categorized with consent
              requirements and the consent-changed event is configured
            </li>
            <li>Enable the appropriate banner below</li>
          </ol>
        </ConsentNote>
      )}
    </>
  );

  return (
    <StandardPageWrapper
      requests={[
        orgRequest,
        privacyCenterRequest,
        cmpCustomizationRequest,
        stagingSettingsRequest,
        scanner.requests.cookieScanRequest,
        scanner.requests.cookieRecommendationsRequest,
        scanner.requests.organizationDataRecipientsRequest,
        scanner.requests.vendorCatalogRequest,
      ]}
      footer={
        // Offered in both environments so onboarding can move on from either one.
        showFooter && (
          <ProgressFooter
            actions={
              <ProgressFooterActions
                nextContent="Done with Banner Configuration"
                nextOnClick={async () => {
                  // On staging, say so first when production doesn't have these settings yet.
                  if (environment === "staging" && productionNeedsPublish()) {
                    setConfirmProductionPublishOpen(true);
                  } else {
                    await finishBannerConfiguration();
                  }
                }}
              />
            }
          />
        )
      }
    >
      <TopNavigationBreadcrumb title="Website Consent" />
      <div className="tw-boundary">
        <TabsWithUnderline
          tabs={tabOptions.map((t) => ({
            name: tabNameByValue[t.value] || String(t.label),
            current: tab === t.value,
            onClick: () => {
              if (
                tab === "consentbannersettings" &&
                t.value !== tab &&
                hasUnpublishedConsentChanges
              ) {
                setPendingTab(t.value);
              } else {
                setTab(t.value);
              }
            },
            labelNode:
              t.value === "truescan" ? (
                <span>
                  TrueScan
                  <span
                    className="text-xs text-neutral-500 ml-1"
                    style={{
                      backgroundColor: "#9581da",
                      padding: "4px",
                      borderRadius: "4px",
                      color: "white",
                      marginLeft: "8px",
                      fontSize: "14px",
                      position: "relative",
                      top: "-2px",
                    }}
                  >
                    Beta
                  </span>
                </span>
              ) : undefined,
          }))}
        />
      </div>
      {pendingTab && (
        <Modal
          title="Discard unpublished changes?"
          body={`Your consent banner changes to ${environment} aren't published. Leaving this tab discards them.`}
          alignText="left"
          alignButton="right"
          closeButton={true}
          onClose={() => setPendingTab(null)}
          firstButton="Keep editing"
          firstButtonColor="tertiary"
          onFirstButtonClick={() => setPendingTab(null)}
          secondButton="Discard changes"
          secondButtonColor="primary"
          onSecondButtonClick={() => {
            setTab(pendingTab);
            setPendingTab(null);
          }}
          showOverlay={true}
          closeOnOverlayClick={false}
        />
      )}
      {confirmProductionPublishOpen && (
        <Modal
          title="Production doesn't have these settings yet"
          body="Your live site won't show this banner until you publish to production."
          alignText="left"
          alignButton="right"
          closeButton={true}
          onClose={() => setConfirmProductionPublishOpen(false)}
          firstButton="Not now"
          firstButtonColor="tertiary"
          onFirstButtonClick={async () => {
            setConfirmProductionPublishOpen(false);
            await finishBannerConfiguration();
          }}
          secondButton="Publish to production"
          secondButtonColor="primary"
          onSecondButtonClick={publishStagingAndProduction}
          showOverlay={true}
          closeOnOverlayClick={false}
        />
      )}
      <TabPanel value="consentbannersettings" tab={tab}>
        <div className="mt-md">
          <ConsentBannerSettings
            // Each environment gets a fresh form; the settings component asks before discarding unpublished edits.
            key={environment}
            ref={consentBannerSettingsRef}
            environment={environment}
            onEnvironmentChange={changeEnvironment}
            focusEnvironmentSwitcher={focusEnvironmentSwitcher}
            onDirtyChange={setHasUnpublishedConsentChanges}
            loadFailed={environment === "staging" && stagingLoadFailed}
            hasSavedSettings={
              environment === "production" ? !neverPublished : stagingSettings != null
            }
            otherEnvironmentSettings={
              environment === "staging" ? settings : stagingLoadFailed ? null : stagingSettings
            }
            intro={consentBannerIntro}
            isConfirmedCustomizableCMPVersion={(privacyCenter?.cmpVersion ?? "") !== ""}
            onUpdateCMPCustomization={updatePrivacyCenterCMPCustomization}
            organization={organization}
            privacyCenterURL={privacyCenter?.url}
            {...environmentSettings}
          />
        </div>
      </TabPanel>
      <TabPanel value="cookiescanner" tab={tab}>
        <CookieScannerTab {...scanner.props} />
      </TabPanel>
      <TabPanel value="truescan" tab={tab}>
        <TrueScanTab />
      </TabPanel>
      <TabPanel value="consentrecords" tab={tab}>
        <ConsentRecordListingWrapper />
      </TabPanel>
      <TabPanel value="consumerconsentlookup" tab={tab}>
        <ConsumerConsentLookup />
      </TabPanel>
      <TabPanel value="gpcoptouts" tab={tab}>
        <GpcOptOutRecords />
      </TabPanel>
    </StandardPageWrapper>
  );
};
